import { useMemo, useState, useEffect } from 'react';
import { View, Modal, Pressable, ScrollView, Alert, Platform, ActionSheetIOS, useWindowDimensions, KeyboardAvoidingView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField } from '@/components/ui/SelectField';
import { ModalHeader } from '@/components/ui/ModalHeader';
import { SubHeading, Body, Caption, ThemedText } from '@/components/ui/Text';
import {
  addCollaboratorByEmail,
  generateInviteLink,
  updateCollaboratorPermission,
  removeCollaborator,
  leaveTrip,
  deactivateInviteLink,
  type InviteLink,
} from '@/services/collaboration';
import type { CollaboratorPermission, Trip, Collaborator } from '@/services/trips';
import { db } from '@/lib/firebase';
import * as Linking from 'expo-linking';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { copyToClipboard } from '@/utils/clipboard';

type CollaborationModalProps = {
  visible: boolean;
  onClose: () => void;
  trip: Trip;
  currentUserId: string;
  onCollaboratorsUpdated?: () => void;
  /** After successfully leaving the trip (e.g. clear selected trip so UI does not show revoked access). */
  onLeftTrip?: () => void;
};

const PERMISSION_OPTIONS = [
  { value: 'view', label: 'View only' },
  { value: 'edit', label: 'Edit trip and all items' },
  { value: 'add_items', label: 'Add and edit own items only' },
] as const;

const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  view: 'Can view trip details, expenses, and itinerary',
  edit: 'Can change trip details, dates, budget, and any collaborator\'s items',
  add_items: 'Can add expenses, bookings, itinerary items; can edit or delete only what they added',
};

export function CollaborationModal({
  visible,
  onClose,
  trip,
  currentUserId,
  onCollaboratorsUpdated,
  onLeftTrip,
}: CollaborationModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const { confirm: confirmAction } = useConfirmDialog();
  const isWeb = Platform.OS === 'web';
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<CollaboratorPermission>('add_items');
  const [loading, setLoading] = useState(false);
  const [addingCollaborator, setAddingCollaborator] = useState(false);
  const [generatingInviteLink, setGeneratingInviteLink] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [inviteLinks, setInviteLinks] = useState<InviteLink[]>([]);
  const [loadingInviteLinks, setLoadingInviteLinks] = useState(false);
  const [showInviteLinkSection, setShowInviteLinkSection] = useState(false);

  const isOwner = trip.owner === currentUserId;
  const collaborators = trip.collaborators || [];

  // Check if user is already a collaborator
  const isCollaborator = collaborators.some((c) => c.userId === currentUserId);

  // Only show if user is owner or collaborator
  if (!isOwner && !isCollaborator) {
    return null;
  }

  const modalBusy = loading || addingCollaborator || generatingInviteLink;

  // Load existing invite links when modal opens (owner only)
  useEffect(() => {
    if (!visible || !isOwner) return;

    const loadInviteLinks = async () => {
      setLoadingInviteLinks(true);
      try {
        // Query for active invite links for this trip
        const invitesQuery = query(
          collection(db, 'tripInvites'),
          where('tripId', '==', trip.id),
          where('isActive', '==', true)
        );
        
        const snapshot = await getDocs(invitesQuery);
        const links: InviteLink[] = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          links.push({
            id: doc.id,
            tripId: data.tripId,
            createdBy: data.createdBy,
            createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
            expiresAt: data.expiresAt
              ? data.expiresAt instanceof Timestamp
                ? data.expiresAt.toDate()
                : new Date(data.expiresAt)
              : undefined,
            permission: data.permission,
            maxUses: data.maxUses,
            usedCount: data.usedCount || 0,
            isActive: data.isActive,
          });
        });
        
        setInviteLinks(links);
        if (links.length > 0) {
          setShowInviteLinkSection(true);
        }
      } catch (error) {
        console.error('[CollaborationModal] Error loading invite links:', error);
      } finally {
        setLoadingInviteLinks(false);
      }
    };

    loadInviteLinks();
  }, [visible, isOwner, trip.id]);

  const handleAddCollaborator = async () => {
    console.log('[CollaborationModal] Add collaborator clicked');
    setFeedback(null);
    
    if (!email.trim()) {
      Alert.alert('Error', 'Please enter an email address');
      return;
    }

    if (!isOwner) {
      Alert.alert('Error', 'Only trip owner can add collaborators');
      return;
    }

    setAddingCollaborator(true);
    try {
      console.log('[CollaborationModal] Calling addCollaboratorByEmail...');
      const result = await withTimeout(
        addCollaboratorByEmail(trip.id, email.trim(), permission, currentUserId),
        15000,
        'Adding collaborator is taking too long. Please try again.'
      );
      console.log('[CollaborationModal] Add collaborator result received');

      if (result.success) {
        setEmail('');
        if (result.isPending) {
          showModalAlert(
            'Invitation sent',
            `${email.trim()} does not have a Tripvio account yet. We've emailed them a secure invitation with a signup link. They'll join this trip automatically when they complete signup using this same email address.`,
          );
        } else {
          showModalAlert(
            'Success! ✅',
            `${result.collaborator?.name || email.trim()} has been added as a collaborator. They will receive a notification and the trip will appear in their trips list.`,
          );
        }
        onCollaboratorsUpdated?.();
      } else {
        const isTripLimit = (result.error || '').toLowerCase().includes('free plan allows 1 active trip');
        if (isTripLimit) {
          showModalAlert(
            'Cannot add collaborator',
            result.error ||
              "This user is on the Free plan and already has an active trip, so they can’t be added to another active trip.",
          );
          return;
        }
        console.error('[CollaborationModal] Failed to add collaborator', { message: result.error || 'unknown' });
        showModalAlert('Error', result.error || 'Failed to add collaborator');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('[CollaborationModal] Error adding collaborator', { message });
      showModalAlert('Error', error instanceof Error ? error.message : 'Failed to add collaborator. Please try again.');
    } finally {
      setAddingCollaborator(false);
    }
  };

  const handleUpdatePermission = async (userId: string, newPermission: CollaboratorPermission) => {
    if (!isOwner) return;

    setFeedback(null);
    setLoading(true);
    try {
      const result = await updateCollaboratorPermission(trip.id, userId, newPermission, currentUserId);

      if (result.success) {
        Alert.alert('Success', 'Permission updated successfully');
        onCollaboratorsUpdated?.();
      } else {
        Alert.alert('Error', result.error || 'Failed to update permission');
      }
    } catch (error) {
      console.error('Error updating permission:', error);
      Alert.alert('Error', 'Failed to update permission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCollaborator = (userId: string, email: string) => {
    if (!isOwner) {
      Alert.alert('Error', 'Only the trip owner can remove collaborators.');
      return;
    }

    if (modalBusy) {
      console.log('[CollaborationModal] Already processing, ignoring click');
      return;
    }

    console.log('[CollaborationModal] Remove collaborator clicked');
    setFeedback(null);

    confirmAction({
      title: 'Remove Collaborator',
      message: `Are you sure you want to remove ${email} from this trip? They will lose access to this trip.`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      onConfirm: async () => {
        console.log('[CollaborationModal] Remove confirmed, starting removal...');
        setLoading(true);
        try {
          console.log('[CollaborationModal] Calling removeCollaborator...');
          const result = await removeCollaborator(trip.id, userId, currentUserId);
          console.log('[CollaborationModal] Remove result:', result);

          if (result.success) {
            // Update collaborators list immediately
            onCollaboratorsUpdated?.();
            // Defer alert to prevent blocking the UI
            setTimeout(() => {
              Alert.alert('Success! ✅', `${email} has been removed from this trip.`);
            }, 0);
          } else {
            console.error('[CollaborationModal] Failed to remove collaborator:', result.error);
            // Defer alert to prevent blocking the UI
            setTimeout(() => {
              Alert.alert('Error', result.error || 'Failed to remove collaborator. Please try again.');
            }, 0);
          }
        } catch (error) {
          console.error('[CollaborationModal] Error removing collaborator:', error);
          const errorMessage = error instanceof Error ? error.message : 'Failed to remove collaborator. Please try again.';
          // Defer alert to prevent blocking
          setTimeout(() => {
            Alert.alert('Error', errorMessage);
          }, 0);
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => {
        console.log('[CollaborationModal] Remove cancelled');
      },
    });
  };

  const handleLeaveTrip = () => {
    confirmAction({
      title: 'Leave trip',
      message: `Leave "${trip.name}"? You will lose access to this trip.`,
      confirmText: 'Leave',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setLoading(true);
        try {
          const result = await leaveTrip(trip.id, currentUserId);
          if (result.success) {
            onLeftTrip?.();
            onClose();
            setTimeout(() => {
              Alert.alert('Left trip', `You have left "${trip.name}".`);
            }, 0);
          } else {
            setTimeout(() => {
              Alert.alert('Error', result.error || 'Failed to leave trip. Please try again.');
            }, 0);
          }
        } catch (error) {
          const msg = error instanceof Error ? error.message : 'Failed to leave trip. Please try again.';
          setTimeout(() => Alert.alert('Error', msg), 0);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleGenerateInviteLink = async () => {
    if (!isOwner) return;

    setGeneratingInviteLink(true);
    try {
      const result = await generateInviteLink(trip.id, permission, currentUserId, 30, undefined);

      if (result.success && result.inviteLink) {
        setInviteLinks((prev) => [result.inviteLink!, ...prev]);
        setShowInviteLinkSection(true);
        Alert.alert('Success', 'Invite link generated successfully');
      } else {
        Alert.alert('Error', result.error || 'Failed to generate invite link');
      }
    } catch (error) {
      console.error('Error generating invite link:', error);
      Alert.alert('Error', 'Failed to generate invite link. Please try again.');
    } finally {
      setGeneratingInviteLink(false);
    }
  };

  const buildInviteUrl = (linkId: string) => {
    if (Platform.OS === 'web') {
      const base = typeof window !== 'undefined' ? window.location.origin : 'https://app.tripvio.app';
      return `${base}/invite/${linkId}`;
    }
    return `tripvio://invite/${linkId}`;
  };

  const handleCopyInviteLink = async (linkId: string) => {
    const inviteUrl = buildInviteUrl(linkId);

    try {
      await copyToClipboard(inviteUrl);
      Alert.alert('Copied', 'Invite link copied to clipboard');
    } catch {
      Alert.alert('Invite Link', inviteUrl, [
        { text: 'OK' },
        { text: 'Copy', onPress: () => {
          void copyToClipboard(inviteUrl);
        }},
      ]);
    }
  };

  const handleDeactivateInviteLink = async (linkId: string) => {
    if (!isOwner) return;
    const runDeactivate = async () => {
      setLoading(true);
      try {
        const result = await deactivateInviteLink(linkId, currentUserId);
        if (result.success) {
          setInviteLinks((prev) => prev.map((link) =>
            link.id === linkId ? { ...link, isActive: false } : link
          ));
          showModalAlert('Success', 'Invite link deactivated');
        } else {
          showModalAlert('Error', result.error || 'Failed to deactivate invite link');
        }
      } catch (error) {
        console.error('Error deactivating invite link:', error);
        showModalAlert('Error', 'Failed to deactivate invite link. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (isWeb && typeof window !== 'undefined') {
      const confirmed = window.confirm('Are you sure you want to deactivate this invite link?');
      if (!confirmed) return;
      void runDeactivate();
      return;
    }

    Alert.alert(
      'Deactivate Link',
      'Are you sure you want to deactivate this invite link?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Deactivate',
          style: 'destructive',
          onPress: () => {
            void runDeactivate();
          },
        },
      ]
    );
  };

  const toDateSafe = (value: unknown): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : null;
    if (value instanceof Timestamp) {
      const d = value.toDate();
      return Number.isFinite(d.getTime()) ? d : null;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const d = new Date(value);
      return Number.isFinite(d.getTime()) ? d : null;
    }
    // Firestore Timestamp-like object (defensive)
    const anyValue = value as any;
    if (typeof anyValue?.toDate === 'function') {
      try {
        const d = anyValue.toDate();
        if (d instanceof Date && Number.isFinite(d.getTime())) return d;
      } catch {
        // ignore
      }
    }
    return null;
  };

  const formatDate = (value: unknown) => {
    const d = toDateSafe(value);
    if (!d) return 'Unknown';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPermissionLabel = (perm: CollaboratorPermission) => {
    return PERMISSION_OPTIONS.find((opt) => opt.value === perm)?.label || perm;
  };

  const isNarrow = windowWidth < 390;
  const withTimeout = async <T,>(task: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(timeoutMessage));
      }, timeoutMs);
    });
    try {
      return await Promise.race([task, timeout]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };
  const showModalAlert = (title: string, message: string) => {
    if (isWeb) {
      const type = title.toLowerCase().includes('error')
        ? 'error'
        : title.toLowerCase().includes('success')
          ? 'success'
          : 'info';
      setFeedback({ type, message });
      return;
    }
    setTimeout(() => {
      Alert.alert(title, message);
    }, 0);
  };

  const permissionActionSheetOptions = useMemo(() => {
    const options = PERMISSION_OPTIONS.map((opt) => opt.label);
    return {
      options: [...options, 'Cancel'],
      cancelButtonIndex: options.length,
    };
  }, []);

  const openPermissionPicker = (userId: string, currentPermission: CollaboratorPermission) => {
    if (!isOwner || modalBusy) return;

    const permOptions = PERMISSION_OPTIONS.map((opt) => opt.value as CollaboratorPermission);
    const currentIndex = permOptions.findIndex((p) => p === currentPermission);

    if (isWeb && typeof window !== 'undefined') {
      const promptText = PERMISSION_OPTIONS.map((opt, index) => `${index + 1}. ${opt.label}`).join('\n');
      const input = window.prompt(
        `Choose what this collaborator can do:\n\n${promptText}\n\nEnter 1, 2, or 3`,
        String(Math.max(1, currentIndex + 1)),
      );
      if (!input) return;
      const selectedIndex = Number(input.trim()) - 1;
      if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || selectedIndex >= permOptions.length) {
        setFeedback({ type: 'error', message: 'Invalid selection. Please enter 1, 2, or 3.' });
        return;
      }
      const nextPermission = permOptions[selectedIndex] ?? currentPermission;
      if (nextPermission !== currentPermission) {
        void handleUpdatePermission(userId, nextPermission);
      }
      return;
    }

    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        {
          title: 'Permission',
          message: 'Choose what this collaborator can do.',
          options: permissionActionSheetOptions.options,
          cancelButtonIndex: permissionActionSheetOptions.cancelButtonIndex,
          destructiveButtonIndex: undefined,
          userInterfaceStyle: 'light',
        },
        (buttonIndex) => {
          if (buttonIndex === permissionActionSheetOptions.cancelButtonIndex) return;
          const nextPermission = permOptions[buttonIndex] ?? currentPermission;
          if (nextPermission !== currentPermission) {
            void handleUpdatePermission(userId, nextPermission);
          }
        },
      );
      return;
    }

    // Android: simple Alert picker
    Alert.alert(
      'Permission',
      'Choose what this collaborator can do.',
      [
        ...PERMISSION_OPTIONS.map((opt) => ({
          text: opt.label + (opt.value === currentPermission ? ' ✓' : ''),
          onPress: () => {
            const nextPermission = opt.value as CollaboratorPermission;
            if (nextPermission !== currentPermission) {
              void handleUpdatePermission(userId, nextPermission);
            }
          },
        })),
        { text: 'Cancel', style: 'cancel' },
      ],
      { cancelable: true },
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      presentationStyle={Platform.OS === 'ios' ? 'fullScreen' : 'overFullScreen'}
      statusBarTranslucent
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.surface,
          justifyContent: 'flex-start',
        }}
      >
        <KeyboardAvoidingView
          enabled={!isWeb}
          behavior={Platform.OS === 'ios' ? 'height' : undefined}
          keyboardVerticalOffset={0}
          style={{ flex: 1, minHeight: 0 }}
        >
          <View
            style={{
              flex: 1,
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              maxHeight: '100%',
              paddingBottom: theme.spacing(2),
              minWidth: 0,
              minHeight: 0,
            }}
          >
          <ModalHeader title="Collaborators" onClose={onClose} />

          {feedback && (
            <View
              style={{
                marginHorizontal: theme.spacing(2),
                marginTop: theme.spacing(1.25),
                paddingHorizontal: theme.spacing(1.5),
                paddingVertical: theme.spacing(1.25),
                borderRadius: theme.radii.md,
                borderWidth: 1,
                borderColor:
                  feedback.type === 'error'
                    ? theme.colors.error + '66'
                    : feedback.type === 'success'
                      ? theme.colors.success + '66'
                      : theme.colors.primary + '55',
                backgroundColor:
                  feedback.type === 'error'
                    ? theme.colors.error + '14'
                    : feedback.type === 'success'
                      ? theme.colors.success + '14'
                      : theme.colors.primary + '14',
              }}
            >
              <Caption
                color={
                  feedback.type === 'error'
                    ? theme.colors.error
                    : feedback.type === 'success'
                      ? theme.colors.success
                      : theme.colors.text
                }
              >
                {feedback.message}
              </Caption>
            </View>
          )}

          <ScrollView
            style={{ flex: 1, minHeight: 0 }}
            contentContainerStyle={{
              flexGrow: 1,
              padding: theme.spacing(2),
              gap: theme.spacing(2),
              paddingBottom: theme.spacing(2) + insets.bottom,
            }}
            keyboardShouldPersistTaps="always"
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
          >
            {/* Trip Info */}
            <Card variant="outlined">
              <SubHeading>{trip.name}</SubHeading>
              {trip.lastEditedBy && toDateSafe((trip as any).lastEditedAt) && (
                <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5) }}>
                  Last edited {formatDate((trip as any).lastEditedAt)}
                </Caption>
              )}
            </Card>

            {/* Add Collaborator Section - Owner Only */}
            {isOwner && (
              <Card variant="outlined">
                <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Add Collaborator</SubHeading>
                <InputField
                  label="Email address"
                  placeholder="user@example.com"
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  style={{ marginBottom: theme.spacing(1.5) }}
                />
                <View style={{ marginBottom: theme.spacing(1) }}>
                  <SelectField
                    label="Permission level"
                    value={permission}
                    options={PERMISSION_OPTIONS}
                    onValueChange={(value) => setPermission(value as CollaboratorPermission)}
                    helperText="What can this collaborator do?"
                    avoidNestedModal={!isWeb}
                  />
                </View>
                <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5), marginTop: -theme.spacing(0.5) }}>
                  {PERMISSION_DESCRIPTIONS[permission]}
                </Caption>
                <Button
                  label="Add Collaborator"
                  onPress={handleAddCollaborator}
                  loading={addingCollaborator}
                  disabled={modalBusy || !email.trim()}
                  icon={<Ionicons name="person-add-outline" size={20} color={theme.colors.surface} />}
                />
              </Card>
            )}

            {/* Generate Invite Link - Owner Only */}
            {isOwner && (
              <Card variant="outlined">
                <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>Invite Link</SubHeading>
                <Body color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5) }}>
                  Create a shareable link that anyone can use to join this trip.
                </Body>
                <View style={{ marginBottom: theme.spacing(1) }}>
                  <SelectField
                    label="Permission for invitees"
                    value={permission}
                    options={PERMISSION_OPTIONS}
                    onValueChange={(value) => setPermission(value as CollaboratorPermission)}
                    avoidNestedModal={!isWeb}
                  />
                </View>
                <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1.5), marginTop: -theme.spacing(0.5) }}>
                  {PERMISSION_DESCRIPTIONS[permission]}
                </Caption>
                <Button
                  label="Generate Invite Link"
                  variant="secondary"
                  onPress={handleGenerateInviteLink}
                  loading={generatingInviteLink}
                  disabled={modalBusy}
                  icon={<Ionicons name="link-outline" size={20} color={theme.colors.text} />}
                />
                
                {/* Display Generated Invite Links */}
                {inviteLinks.length > 0 && (
                  <View style={{ marginTop: theme.spacing(2), gap: theme.spacing(1) }}>
                    <SubHeading style={{ fontSize: 14 }}>Active Invite Links</SubHeading>
                    {inviteLinks.map((link) => {
                      const inviteUrl = buildInviteUrl(link.id);
                      
                      return (
                        <View
                          key={link.id}
                          style={{
                            padding: theme.spacing(1.5),
                            borderRadius: theme.radii.md,
                            borderWidth: 1,
                            borderColor: theme.colors.border,
                            backgroundColor: theme.colors.surface,
                            gap: theme.spacing(1),
                          }}
                        >
                          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                            <View style={{ flex: 1 }}>
                              <ThemedText variant="bodyStrong" style={{ fontSize: 12 }}>
                                {getPermissionLabel(link.permission)}
                              </ThemedText>
                              <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                                {link.expiresAt ? `Expires: ${formatDate(link.expiresAt)}` : 'No expiration'}
                                {link.maxUses ? ` • ${link.maxUses - link.usedCount} uses left` : ''}
                              </Caption>
                            </View>
                            {link.isActive && (
                              <View style={{ flexDirection: 'row', gap: theme.spacing(0.5) }}>
                                <Pressable
                                  onPress={() => handleCopyInviteLink(link.id)}
                                  disabled={modalBusy}
                                  style={{
                                    padding: theme.spacing(0.75),
                                    borderRadius: theme.radii.md,
                                    backgroundColor: theme.colors.primary + '20',
                                    opacity: modalBusy ? 0.5 : 1,
                                  }}
                                >
                                  <Ionicons name="copy-outline" size={18} color={theme.colors.primary} />
                                </Pressable>
                                <Pressable
                                  onPress={() => handleDeactivateInviteLink(link.id)}
                                  disabled={modalBusy}
                                  style={{
                                    padding: theme.spacing(0.75),
                                    borderRadius: theme.radii.md,
                                    backgroundColor: theme.colors.error + '20',
                                    opacity: modalBusy ? 0.5 : 1,
                                  }}
                                >
                                  <Ionicons name="close-circle-outline" size={18} color={theme.colors.error} />
                                </Pressable>
                              </View>
                            )}
                          </View>
                          {!link.isActive && (
                            <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
                              Inactive
                            </Caption>
                          )}
                        </View>
                      );
                    })}
                  </View>
                )}
              </Card>
            )}

            {/* Existing Collaborators */}
            <Card variant="outlined">
              <SubHeading style={{ marginBottom: theme.spacing(1.5) }}>
                Collaborators ({collaborators.length})
              </SubHeading>
              {collaborators.length === 0 ? (
                <Body color={theme.colors.textSecondary}>No collaborators yet.</Body>
              ) : (
                <View style={{ gap: theme.spacing(1.5) }}>
                  {collaborators.map((collab) => (
                    <View
                      key={collab.userId}
                      style={{
                        flexDirection: 'column',
                        gap: theme.spacing(1),
                        padding: theme.spacing(1.25),
                        borderRadius: theme.radii.md,
                        borderWidth: 1,
                        borderColor: theme.colors.border,
                        backgroundColor: theme.colors.surface,
                      }}
                    >
                      <View style={{ flexDirection: isNarrow ? 'column' : 'row', gap: theme.spacing(1), alignItems: isNarrow ? 'flex-start' : 'center' }}>
                        <View style={{ flex: 1, minWidth: 0 }}>
                          <ThemedText variant="bodyStrong" numberOfLines={1}>
                            {collab.name || collab.email}
                          </ThemedText>
                        <Caption
                          color={theme.colors.textSecondary}
                          style={{ flexShrink: 1, flexWrap: 'wrap' }}
                        >
                          {collab.email}
                        </Caption>
                        <Caption color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.25) }}>
                          {getPermissionLabel(collab.permission)} • Added {formatDate((collab as any).addedAt)}
                        </Caption>
                      </View>

                        {isOwner && (
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: theme.spacing(0.75),
                              marginTop: isNarrow ? theme.spacing(0.5) : 0,
                            }}
                          >
                            <Pressable
                              onPress={() => openPermissionPicker(collab.userId, collab.permission)}
                              disabled={modalBusy}
                              accessibilityRole="button"
                              accessibilityLabel={`Permission: ${getPermissionLabel(collab.permission)}`}
                              style={({ pressed }) => ({
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: theme.spacing(0.5),
                                paddingHorizontal: theme.spacing(1),
                                paddingVertical: theme.spacing(0.75),
                                borderRadius: theme.radii.pill,
                                borderWidth: 1,
                                borderColor: theme.colors.primary + '55',
                                backgroundColor: theme.colors.primary + '15',
                                opacity: modalBusy ? 0.5 : pressed ? 0.8 : 1,
                                minHeight: 36,
                              })}
                            >
                              <Ionicons name="options-outline" size={16} color={theme.colors.primary} />
                              <Ionicons name="chevron-down" size={16} color={theme.colors.primary} />
                            </Pressable>

                            <Pressable
                              onPress={() => {
                                if (!modalBusy) {
                                  handleRemoveCollaborator(collab.userId, collab.email);
                                }
                              }}
                              disabled={modalBusy}
                              style={({ pressed }) => ({
                                width: 36,
                                height: 36,
                                borderRadius: theme.radii.md,
                                backgroundColor: theme.colors.error + '15',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: modalBusy ? 0.5 : pressed ? 0.7 : 1,
                              })}
                            >
                              <Ionicons name="trash-outline" size={20} color={theme.colors.error} />
                            </Pressable>
                          </View>
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              )}
            </Card>

            <View style={{ flexDirection: 'row', gap: theme.spacing(1.5) }}>
              {isCollaborator && !isOwner && (
                <Button
                  label="Leave trip"
                  variant="secondary"
                  onPress={handleLeaveTrip}
                  disabled={modalBusy}
                  style={{ flex: 1 }}
                  icon={<Ionicons name="exit-outline" size={20} color={theme.colors.error} />}
                />
              )}
              <Button
                label="Close"
                variant="ghost"
                onPress={onClose}
                disabled={modalBusy}
                style={{ flex: 1 }}
              />
            </View>
          </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

